<template>
  <div class="firmware-manage">
    <el-card>
      <div slot="header" class="card-header">
        <span>固件管理</span>
        <el-button type="primary" size="small" @click="handleUploadDialog">
          <i class="el-icon-upload2"></i> 上传固件
        </el-button>
      </div>

      <el-alert
        title="固件包需使用 Luatools 生成「远程升级文件」(.bin)，版本号需与设备 main.lua 中的 VERSION 保持一致。上传后在「设备列表」勾选多台设备即可批量一键下发升级；离线设备上线后会自动补发升级。"
        type="info"
        :closable="false"
        style="margin-bottom: 15px;">
      </el-alert>

      <el-table :data="firmwareList" border style="width: 100%" v-loading="loading">
        <el-table-column type="index" label="序号" width="70"></el-table-column>
        <el-table-column prop="version" label="版本号" width="120">
          <template slot-scope="scope">
            <el-tag type="success" size="small">v{{ scope.row.version }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="deviceType" label="适用设备类型" width="140">
          <template slot-scope="scope">
            {{ scope.row.deviceType || '通用' }}
          </template>
        </el-table-column>
        <el-table-column prop="originalName" label="固件文件" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column prop="fileSize" label="文件大小" width="110">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.fileSize) }}
          </template>
        </el-table-column>
        <el-table-column prop="uploadTime" label="上传时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.uploadTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 上传固件对话框 -->
    <el-dialog title="上传固件" :visible.sync="uploadDialogVisible" width="520px" :close-on-click-modal="false" @close="resetUploadForm">
      <el-form :model="uploadForm" :rules="uploadRules" ref="uploadForm" label-width="110px">
        <el-form-item label="固件文件" prop="file">
          <el-upload
            ref="uploader"
            action=""
            :auto-upload="false"
            :limit="1"
            accept=".bin"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :on-exceed="handleExceed">
            <el-button size="small" type="primary" icon="el-icon-folder-opened">选择 .bin 文件</el-button>
            <div slot="tip" class="el-upload__tip">仅限 Luatools 生成的远程升级 .bin 文件；命名规则：设备类型_版本号_xxx.bin（如 YR_4G02_220_0.1.1_LuatOS-SoC_V2050_Air780EPM.bin），选择后自动识别填充</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="版本号" prop="version">
          <el-input v-model="uploadForm.version" placeholder="如 1.0.1，需与设备 main.lua 的 VERSION 一致"></el-input>
        </el-form-item>
        <el-form-item label="适用设备类型">
          <el-input v-model="uploadForm.deviceType" placeholder="留空表示通用固件"></el-input>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="uploadForm.remark" type="textarea" :rows="2" placeholder="本次更新内容说明（可选）"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="uploadDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="handleUploadSubmit">
          {{ uploading ? '上传中...' : '开始上传' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'FirmwareManage',
  data() {
    return {
      firmwareList: [],
      loading: false,
      uploadDialogVisible: false,
      uploading: false,
      uploadForm: {
        version: '',
        deviceType: '',
        remark: '',
        file: null
      },
      uploadRules: {
        version: [{ required: true, message: '请输入固件版本号', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.fetchFirmwares()
  },
  methods: {
    async fetchFirmwares() {
      this.loading = true
      try {
        const res = await api.getFirmwares()
        this.firmwareList = res.data || []
      } catch (error) {
        this.$message.error('获取固件列表失败')
        this.firmwareList = []
      } finally {
        this.loading = false
      }
    },
    handleUploadDialog() {
      this.uploadDialogVisible = true
    },
    handleFileChange(file) {
      this.uploadForm.file = file.raw
      // 从文件名自动识别版本号与适用设备类型
      // 命名规则：{设备类型}_{版本号}_其他信息.bin，如 YR_4G02_220_0.1.1_LuatOS-SoC_V2050_Air780EPM.bin
      const name = file.name || ''
      const base = name.replace(/\.bin$/i, '')
      const segs = base.split('_')
      const idx = segs.findIndex(s => /^\d+(\.\d+)+$/.test(s))
      if (idx > 0) {
        const version = segs[idx]
        const deviceType = segs.slice(0, idx).join('_')
        this.uploadForm.version = version
        this.uploadForm.deviceType = deviceType
        this.$message.success(`已从文件名自动识别：版本 v${version}，适用设备类型 ${deviceType}`)
      } else {
        this.$message.info('未能从文件名识别版本号，请手动填写（规则：设备类型_版本号_xxx.bin）')
      }
    },
    handleFileRemove() {
      this.uploadForm.file = null
    },
    handleExceed() {
      this.$message.warning('只能上传一个固件文件，请先移除已选文件')
    },
    handleUploadSubmit() {
      this.$refs.uploadForm.validate(valid => {
        if (!valid) return
        if (!this.uploadForm.file) {
          this.$message.error('请选择固件 .bin 文件')
          return
        }
        const formData = new FormData()
        formData.append('file', this.uploadForm.file)
        formData.append('version', this.uploadForm.version.trim())
        formData.append('deviceType', this.uploadForm.deviceType || '')
        formData.append('remark', this.uploadForm.remark || '')

        this.uploading = true
        api.uploadFirmware(formData)
          .then(res => {
            if (res.success) {
              this.$message.success('固件上传成功')
              this.uploadDialogVisible = false
              this.fetchFirmwares()
            } else {
              this.$message.error(res.message || '固件上传失败')
            }
          })
          .catch(error => {
            this.$message.error('固件上传失败：' + (error.response?.data?.message || error.message))
          })
          .finally(() => {
            this.uploading = false
          })
      })
    },
    resetUploadForm() {
      this.uploadForm = { version: '', deviceType: '', remark: '', file: null }
      if (this.$refs.uploader) {
        this.$refs.uploader.clearFiles()
      }
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.resetFields()
      }
    },
    handleDelete(row) {
      this.$confirm(`确定要删除固件 v${row.version} 吗？删除后不可恢复。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        api.deleteFirmware(row.id)
          .then(res => {
            if (res.success) {
              this.$message.success('固件删除成功')
              this.fetchFirmwares()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          })
          .catch(error => {
            this.$message.error('删除失败：' + (error.response?.data?.message || error.message))
          })
      }).catch(() => {})
    },
    formatFileSize(bytes) {
      if (!bytes) return '-'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1024 / 1024).toFixed(2) + ' MB'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.danger-text {
  color: #F56C6C;
}
</style>
